/**
 * Aggregate Semester Dashboard (Phase 2 — Step 2.10).
 *
 * Shows cross-course totals, risk distribution, GPA projection.
 */

import React from 'react';
import { useAppStore } from '@/store';
import { RiskBadge, StatCard } from '../shared';
import type { RiskLevel } from '@abs/shared-types';
import { selectCoursesWithBudget, selectPerAbsenceGPACost } from '@/store/selectors';
import { GpaSkipAllocatorPanel } from './GpaSkipAllocatorPanel';

export function SemesterDashboard(): React.ReactElement {
  const state = useAppStore.getState();
  const coursesWithBudget = selectCoursesWithBudget(state);

  if (coursesWithBudget.length === 0) {
    return (
      <div className="semester-dashboard semester-dashboard--empty">
        <p>Add courses to see your semester overview.</p>
      </div>
    );
  }

  // Aggregate metrics
  const totalAbsences = coursesWithBudget.reduce((sum, c) => sum + c.budget.usedTotal, 0);
  const totalRemaining = coursesWithBudget.reduce((sum, c) => sum + c.budget.remaining, 0);

  // Risk distribution
  const riskDistribution: Record<RiskLevel, number> = {
    satisfactory: 0,
    warning: 0,
    at_risk: 0,
    critical: 0,
  };
  let highestRiskCourse: { course: string; risk: RiskLevel } | null = null;

  for (const { course, budget } of coursesWithBudget) {
    riskDistribution[budget.riskLevel]++;
    if (!highestRiskCourse || riskRank(budget.riskLevel) > riskRank(highestRiskCourse.risk)) {
      highestRiskCourse = { course: course.name, risk: budget.riskLevel };
    }
  }

  // Highest GPA impact (TODO-14) — credit-hour-weighted per-absence GPA cost
  const gpaCosts = selectPerAbsenceGPACost(state);
  const topGpaImpact = gpaCosts.length > 0
    ? gpaCosts.reduce((a, b) => (b.gpaCostPerAbsence > a.gpaCostPerAbsence ? b : a), gpaCosts[0])
    : null;
  const differsFromHighestRisk =
    !!topGpaImpact && !!highestRiskCourse && topGpaImpact.courseName !== highestRiskCourse.course;

  return (
    <div className="semester-dashboard" role="region" aria-label="Semester Dashboard">
      <h3>Semester Overview</h3>

      <div className="semester-dashboard__stats">
        <StatCard variant="card" size="lg" className="semester-dashboard__stat" value={totalAbsences} label="Total Absences" />
        <StatCard variant="card" size="lg" className="semester-dashboard__stat" value={totalRemaining} label="Remaining Budget" />
        <StatCard variant="card" size="lg" className="semester-dashboard__stat" value={coursesWithBudget.length} label="Active Courses" />
      </div>

      <div className="semester-dashboard__risk-grid">
        <h4>Risk Distribution</h4>
        <div className="semester-dashboard__risk-items">
          {(Object.entries(riskDistribution) as [RiskLevel, number][])
            .filter(([, count]) => count > 0)
            .map(([level, count]) => (
              <div key={level} className="semester-dashboard__risk-item">
                <RiskBadge level={level} />
                <span>{count} {count === 1 ? 'course' : 'courses'}</span>
              </div>
            ))}
        </div>
      </div>

      {(highestRiskCourse || topGpaImpact) && (
        <div className="semester-dashboard__highest-risk">
          {highestRiskCourse && (
            <div>
              <strong>Highest Risk:</strong> {highestRiskCourse.course}
              <RiskBadge level={highestRiskCourse.risk} />
            </div>
          )}
          {topGpaImpact && (
            <div>
              <strong>Highest GPA Impact:</strong> {topGpaImpact.courseName}
              {differsFromHighestRisk && (
                <span className="stat-caption"> (differs from Highest Risk — this course weighs more on your GPA.)</span>
              )}
            </div>
          )}
        </div>
      )}

      <GpaSkipAllocatorPanel />

      <div className="semester-dashboard__course-list">
        <h4>Courses</h4>
        {coursesWithBudget.map(({ course, budget }) => (
          <div key={course.id} className="semester-dashboard__course-row">
            <span className="semester-dashboard__course-name">{course.name} ({course.code})</span>
            <span className="semester-dashboard__course-pct">{budget.attendancePercentage.toFixed(1)}%</span>
            <RiskBadge level={budget.riskLevel} />
            <span className="semester-dashboard__course-budget">{budget.remaining} left</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function riskRank(level: RiskLevel): number {
  const ranks: Record<RiskLevel, number> = {
    satisfactory: 0,
    warning: 1,
    at_risk: 2,
    critical: 3,
  };
  return ranks[level];
}